import React, { useRef, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from './timeFilter.module.scss';
import { useClickOutside, useFixedDropdownPosition } from '../../../utils';
import { Button, Icon } from '../..';
import FilterTitle from '../_elements/filterTitle/filterTitle';

const hasSelectedTime = (from, to) => from !== '00:00' || to !== '00:00';

const TimeFilter = ({
  title,
  lt,
  setLt,
  gt,
  setGt,
  onSubmit,
  sortOrder,
  setSortOrder,
  name,
  position,
  inLocations,
  isActive,
  disableSorting,
  disabled,
  icon = 'filter',
  iconClass = '',
}) => {
  const filterRef = useRef();
  const dropdownRef = useRef();
  const [isOpen, setIsOpen] = useClickOutside(filterRef);
  const [shouldSort, setShouldSort] = useState(false);
  const dropdownStyle = useFixedDropdownPosition({
    triggerRef: filterRef,
    dropdownRef,
    isOpen,
    position,
  });
  const isTimeSelected = hasSelectedTime(gt, lt);

  const handleTitleClick = () => {
    if (disabled) {
      return;
    }

    if (!isOpen) {
      setShouldSort(false);
    }
    isOpen ? setIsOpen(false) : setIsOpen(true);
  };

  const handleOrderClick = () => {
    if (disabled) {
      return;
    }

    setShouldSort(true);
    sortOrder === 'asc' ? setSortOrder('desc') : setSortOrder('asc');
  };

  const handleSubmit = () => {
    if (disabled) {
      return;
    }

    setIsOpen(false);
    onSubmit(shouldSort ? { sortBy: name } : {});
  };

  return (
    <div className={styles.container} ref={filterRef}>
      {inLocations ? (
        <div className={styles.locationsHeader} onClick={handleTitleClick}>
          {isTimeSelected ? (
            `${gt} - ${lt}`
          ) : (
            <div className={cn(styles.title, styles.rangeTitle)}>
              <p>{title}</p>
              <Icon
                name={icon}
                classes={{ icon: cn(styles.icon, iconClass) }}
              />
            </div>
          )}
        </div>
      ) : (
        <FilterTitle
          title={title}
          onTitleClick={handleTitleClick}
          isActive={isActive}
          disabled={disabled}
        />
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(styles.filter, styles[position])}
          style={dropdownStyle}
        >
          <div className={styles.range}>
            <input
              className={styles.input}
              type="time"
              min="0:00"
              max="24:00"
              value={gt}
              disabled={disabled}
              onChange={e => setGt(e.target.value)}
            />
            <input
              className={styles.input}
              type="time"
              min="0:00"
              max="24:00"
              value={lt}
              disabled={disabled}
              onChange={e => setLt(e.target.value)}
            />
          </div>

          <div className={styles.actions}>
            {!disableSorting && (
              <Button
                color="light"
                classes={{ button: styles.button }}
                onClick={handleOrderClick}
                disabled={disabled}
              >
                {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
              </Button>
            )}
            <Button
              color="primary"
              classes={{ button: styles.button }}
              onClick={handleSubmit}
              disabled={disabled}
            >
              Filtruoti
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

TimeFilter.defaultProps = {
  position: 'left',
  inLocations: false,
  disabled: false,
};

TimeFilter.propTypes = {
  title: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  lt: PropTypes.string.isRequired,
  setLt: PropTypes.func.isRequired,
  gt: PropTypes.string.isRequired,
  setGt: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sortOrder: PropTypes.oneOf(['asc', 'desc']).isRequired,
  setSortOrder: PropTypes.func.isRequired,
  position: PropTypes.string,
  inLocations: PropTypes.bool,
  isActive: PropTypes.bool.isRequired,
  disableSorting: PropTypes.bool,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  iconClass: PropTypes.string,
};

export default TimeFilter;
